import {
  LocationType,
  MarketingType,
  PropertyKind,
  PropertyType,
} from '@ev/search-api';
import { useRouter } from 'next/router';
import type { ChangeEvent } from 'react';
import React, { useEffect, useRef, useState } from 'react';

import type { ITextRadioProps, SelectOption } from '@/components/elements';
import { SelectWithIcon } from '@/components/elements';
import {
  useIsSmallerThan,
  useKeyboardNavigation,
  useOnClickOutside,
} from '@/components/utils';
import SearchProperty from '@/icons/small/Search.svg';
import { type TrackingInfo, useSnowplow } from '@/lib';
import type { PropertyTypes } from '@/utils';
import {
  appendSphParam,
  fetchSuggestions,
  getSlugFromPath,
  marketingTypeValues,
  SEARCH_WEBSITE,
} from '@/utils';

import {
  StyledContainer,
  StyledForm,
  StyledIconButton,
  StyledSearchInputWrapper,
  StyledTextRadio,
} from './SearchForm.styled';
import { SearchInput } from './SearchInput';
import { SuggestionList } from './SuggestionList';
import { fetchSearchLink } from './utils/fetchSearchLink';

const MIN_CHARACTERS_FOR_SUGGESTIONS = 3;

export type LocationTypeCombined = LocationType | 'displayId';

export type CombinedSuggestion = {
  id?: string;
  type?: LocationTypeCombined;
  description: string;
  isDisplayId?: boolean;
  marketingTitle?: string;
};

export type SearchFilters = {
  marketingType?: MarketingType;
  objectType?: PropertyType;
  kind?: PropertyKind;
};

interface SearchFormProps {
  placeholder: string;
  error?: boolean;
  className?: string;
  propertyTypeOptions: SelectOption[];
  propertyTypeButtonLabel: string;
  marketingTypeOptions: ITextRadioProps[];
  marketingTypeLabel: string;
  trackingInfo?: TrackingInfo;
}

export const SearchForm = ({
  className,
  placeholder,
  propertyTypeOptions,
  propertyTypeButtonLabel,
  marketingTypeOptions,
  marketingTypeLabel,
  trackingInfo,
}: SearchFormProps) => {
  const router = useRouter();
  const { asPath } = router;
  const { language = 'en', tenant } = getSlugFromPath(asPath);
  const isTablet = useIsSmallerThan('LAPTOP');
  const isMobile = useIsSmallerThan('TABLET_PORTRAIT');

  const [query, setQuery] = useState('');
  const [focusedSuggestionIndex, setFocusedSuggestionIndex] = useState(0);
  const [firstSuggestion, setFirstSuggestion] =
    useState<CombinedSuggestion | null>(null);
  const [suggestions, setSuggestions] = useState<CombinedSuggestion[]>([]);
  const [propertyFilter, setPropertyFilter] = useState<SearchFilters>({
    marketingType: undefined,
    objectType: undefined,
    kind: undefined,
  });
  const [isNavigating, setIsNavigating] = useState(false);

  const [offset, setOffset] = useState(0);
  const containerRef = useRef<HTMLDivElement | null>(null);
  const suggestionsRef = useRef<HTMLUListElement | null>(null);
  const inputFieldRef = useRef<HTMLInputElement | null>(null);
  const { snowplow } = useSnowplow();

  const closeSuggestions = () => setSuggestions([]);

  useOnClickOutside(suggestionsRef, closeSuggestions);

  useEffect(() => {
    setOffset(containerRef?.current?.getBoundingClientRect().top || 0);
    const onScroll = () => {
      if (containerRef?.current) {
        setOffset(containerRef?.current?.getBoundingClientRect().top);
      }
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const fetchLocationSuggestions = async (query: string) => {
    if (query.length < MIN_CHARACTERS_FOR_SUGGESTIONS) {
      setSuggestions([]);
      return;
    }
    try {
      const locationSuggestions = await fetchSuggestions(query, language);
      setSuggestions(locationSuggestions);

      // Saves first suggestion so search works when user does not select an option
      setFirstSuggestion(locationSuggestions[0]);
    } catch (error) {
      console.error('Error fetching location suggestions:', error);
    }
  };

  const onChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setQuery(value);
    fetchLocationSuggestions(value);
  };

  // Simulate a delay to show a selected suggestion before navigate to page
  const navigateToSearchPageUrl = (url: string) => {
    setIsNavigating(true);
    setTimeout(() => {
      window.open(url, '_self', 'noopener');
    }, 500);
  };

  const navigateToSuggestionSearchPage = async (
    suggestion: CombinedSuggestion
  ) => {
    await snowplow.trackSearchEvent({
      searchTermTyped: query,
      searchTermSelected: suggestion,
      selectedTermPosition: focusedSuggestionIndex,
      totalSearchTerms: suggestions.length,
      propertyType: propertyFilter.objectType || 'all',
      searchIntent: propertyFilter.marketingType || marketingTypeValues.buy,
    });

    if (suggestion?.description) {
      setQuery(suggestion.description);
      setSuggestions([]);

      const searchLink = await fetchSearchLink(
        tenant,
        suggestion,
        language,
        propertyFilter
      );
      navigateToSearchPageUrl(searchLink);
    }
  };

  // keyboard navigation for the suggestion list
  const { handleKeyDown } = useKeyboardNavigation(
    focusedSuggestionIndex,
    setFocusedSuggestionIndex,
    suggestions,
    navigateToSuggestionSearchPage
  );

  // mouse navigation for the suggestion list
  const handleSuggestionMouseEnter = (index: number) => {
    setFocusedSuggestionIndex(index);
  };

  const handleSuggestionMouseLeave = () => {
    setFocusedSuggestionIndex(0);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (firstSuggestion) {
      await navigateToSuggestionSearchPage(firstSuggestion);
      return;
    }

    const marketingTypeValue =
      propertyFilter.marketingType || marketingTypeValues.buy;

    const kind = propertyFilter.kind ? `kind=${propertyFilter.kind}` : null;

    const objectType = propertyFilter.objectType
      ? `objectType=${propertyFilter.objectType}`
      : null;
    const marketingType = `marketingType=${marketingTypeValue}`;

    await snowplow.trackSearchEvent({
      propertyType: propertyFilter.objectType || 'all',
      searchIntent: marketingTypeValue,
    });

    const url = appendSphParam(
      router,
      `${SEARCH_WEBSITE}/${language}/propertysearch?${
        (kind || objectType) ?? ''
      }&${marketingType}`
    );

    snowplow.trackNavigationCustomEvent({
      ...trackingInfo,
      href: url,
    });

    window.open(url, '_self', 'noopener, noreferrer');
  };

  const cleanInput = () => {
    setQuery('');
    setFirstSuggestion(null);

    inputFieldRef?.current?.focus();
  };

  const onChangePropertyType = (value: PropertyTypes) => {
    const typeFilters: SearchFilters = {};

    if (value === 'all') {
      typeFilters.kind = undefined;
      typeFilters.objectType = undefined;
    } else if (value === 'group') {
      typeFilters.kind = 'group';
      typeFilters.objectType = undefined;
    } else {
      typeFilters.kind = undefined;
      typeFilters.objectType = value;
    }

    setPropertyFilter((prevFilters) => ({
      ...prevFilters,
      ...typeFilters,
    }));
  };

  const onChangeMarketingType = (value: string) => {
    setPropertyFilter((prevFilters) => ({
      ...prevFilters,
      marketingType: value as MarketingType,
    }));
  };

  return (
    <StyledContainer ref={containerRef}>
      <StyledTextRadio
        items={marketingTypeOptions}
        defaultValue={marketingTypeOptions[0].value}
        onChange={onChangeMarketingType}
        label={marketingTypeLabel}
      />
      <StyledForm className={className} onSubmit={handleSubmit}>
        <SelectWithIcon
          options={propertyTypeOptions}
          onChange={onChangePropertyType}
          buttonLabel={propertyTypeButtonLabel}
        />
        <StyledSearchInputWrapper>
          <SearchInput
            query={query}
            placeholder={placeholder}
            ref={inputFieldRef}
            onChange={onChange}
            onKeyDown={handleKeyDown}
            cleanInput={cleanInput}
            onFocus={() => {
              if (isTablet && offset >= 128 && containerRef?.current) {
                window.scrollTo({
                  behavior: 'smooth',
                  top: containerRef?.current?.offsetTop - 40,
                });
              }
            }}
          />
        </StyledSearchInputWrapper>
        <StyledIconButton
          shape="square"
          icon={<SearchProperty />}
          variant="search"
          size={isMobile ? 'small' : 'medium'}
          label="search"
          type="submit"
          forwardedAs="button"
          $isActive={isNavigating}
        />
      </StyledForm>
      {suggestions.length > 0 && (
        <SuggestionList
          query={query}
          suggestions={suggestions}
          focusedSuggestionIndex={focusedSuggestionIndex}
          handleClickOutside={closeSuggestions}
          handleSuggestionClick={navigateToSuggestionSearchPage}
          handleSuggestionMouseEnter={handleSuggestionMouseEnter}
          handleSuggestionMouseLeave={handleSuggestionMouseLeave}
        />
      )}
    </StyledContainer>
  );
};
